import {
  AlertColor,
  Box,
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
} from '@mui/material';
import TitleText from '../../components/formlib/TitleText';
import ProgramPanel from '../../components/forms/ProgramPanel';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AreaOfConcerns,
  ClinicalEligibilty,
  ClinicalJudgement,
  Tools,
} from './types';
import KeyValueComponent, {
  DataJsonType,
} from '../../components/forms/KeyValueComponent';
import MediumTypography from '../../components/formlib/MediumTypography';
import { HeadCell, TableHeader } from '../../components/formlib/TableHeader';
import SelectComponent from '../../components/formlib/SelectComponent';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import CommentTextbox from '../../components/formlib/CommentTextbox';
import { useIntl } from 'react-intl';
import AreaOfConcern from './AreaOfConcern';
import { ReactComponent as AddNew } from '../../assets/images/Plus.svg';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import DropdownComponent from '../../components/formlib/DropdownComponent';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import { ReactComponent as DeleteAlertIcon } from '../../assets/images/deleteAlertIcon.svg';
import {
  EmployeeListType,
  getAllEmployeeMinimalList,
} from '../../services/ifspClient/IfspApi';
import {
  ApiError,
  ApiMessage,
  isCustomError,
} from '../../services/ApiResponseHandler';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import {
  deleteConcernById,
  deleteTeamMemberById,
  getClientClinicalJudgementDetails,
  saveClinicalJudgement,
} from '../../services/configApi/eligibility/clinicalEligibility';
import { useLocation, useNavigate } from 'react-router';
import * as yup from 'yup';
import { Formik, FormikProps, getIn } from 'formik';
import ActionToolTip from '../../components/formlib/ActionToolTip';
import { ReactComponent as DeleteIcon } from '../../assets/images/DeleteIcon.svg';
import Textinput from '../../components/formlib/Textinput';
import dayjs from 'dayjs';
import SignatureComponent from '../../components/formlib/modal/SignatureComponent';
import ModalPopup from '../../components/formlib/ModalPopup';
import { ReactComponent as Backarrow } from '../../assets/images/backarrow.svg';
import { ReactComponent as Editsignature } from '../../assets/images/editsignature.svg';
import _, { cloneDeep, debounce } from 'lodash';
import { BATELLE_DEVELOPMENT_INVENTORY_2 } from '../../routes/Routing';
import CustomcardComponent from '../../components/formlib/CustomCardComponent';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import { LookUpContext } from '../../context/LookUpContextProvider';
import { OptionType } from '../../utils/type';
import { UnSavedChangesContext } from '../../context/UnSavedChangesProvider';
import { datePickerMinDateMaxDateValidate } from '../../utils/dateUtil';
import { formatName } from '../../utils/nameUtils';
import moment from 'moment';
import {
  LOCAL_DATE_TIME_FORMAT,
  REFERRAL_STATUS_KEY,
} from '../../services/Constant';
import { useMultiplePermissions } from '../../hooks/usePermissions';
import LazyAutocompleteSearch from '../../components/formlib/LazyAutocompleteSearch';
import { getAllEmployeeByName } from '../../services/configApi/employees/employeeServices';

const teamHeadCells: HeadCell<TableData>[] = [
  {
    id: 'name',
    labelId: 'Name',
    defaultLabelId: 'Name',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'disciplineType',
    labelId: 'disciplineText',
    defaultLabelId: 'Discipline',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'email',
    labelId: 'Email',
    defaultLabelId: 'Email',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'phoneNumber',
    labelId: 'Phone Number',
    defaultLabelId: 'Phone Number',
    numeric: false,
    requiredSorting: false,
  },
  {
    id: 'actions',
    labelId: 'Actions',
    defaultLabelId: 'Actions',
    numeric: false,
    requiredSorting: false,
  },
];

interface TableData {
  name: string;
  disciplineType: string;
  email: string;
  phoneNumber: string;
  actions: string;
}

const validationSchema = yup.object().shape({
  teams: yup.array().of(
    yup.object().shape({
      name: yup.string().nullable(),
      disciplineType: yup.string().nullable(),
      email: yup.string(),
      phoneNumber: yup.string(),
    }),
  ),
  additionalTools: yup
    .object()
    .shape({
      tools: yup.array().of(
        yup.object().shape({
          nameOfToolUsed: yup.string().nullable(),
          dateAdministered: yup
            .string()
            .test({
              name: 'dateAdministered',
              test: function (value) {
                const dateAdministered = this.resolve(
                  yup.ref('dateAdministered'),
                );
                if (value && dateAdministered) {
                  const pickerMin = '01/01/1900';
                  const pickerMax = '12/31/2099';
                  if (
                    dayjs(value).isBefore(pickerMin) ||
                    dayjs(value).isAfter(pickerMax)
                  ) {
                    return this.createError({
                      path: this.path,
                      message: 'datePickerMinDateMaxDateValidate',
                    });
                  }
                }
                return true;
              },
            })
            .nullable(),
          administeredBy: yup.string().nullable(),
          disciplineOfPerson: yup
            .string()
            .nullable()
            .when('administeredBy', {
              is: (val: string | null | undefined) =>
                val != null && val.trim() !== '',
              then: (schema) => schema.required('disciplineOfPersonRequired'),
            }),
          specifyOther: yup.string().nullable(),
        }),
      ),
      additionalToolComments: yup.string().nullable(),
    })
    .nullable(),
  notes: yup.string().required('ClinicalJudgement.ConcernsValidation'),
  concerns: yup.array().of(
    yup.object().shape({
      areaOfConcern: yup.string().nullable(),
      teamMember: yup.string().nullable(),
      comments: yup.string().nullable(),
    }),
  ),
  signature: yup.object().shape({
    signatureValue: yup.string(),
    partitionKey: yup.string(),
    rowKey: yup.string(),
    name: yup.string().nullable(),
    date: yup.date().nullable(),
    signature: yup.string().nullable(),
  }),
});

const initialValue: ClinicalEligibilty = cloneDeep({
  teams: [],
  additionalTools: {
    tools: [
      {
        additionalToolsId: '',
        nameOfToolUsed: null,
        dateAdministered: null,
        administeredBy: null,
        disciplineOfPerson: null,
        specify: null,
        status: null,
      },
    ],
    additionalToolComments: null,
  },
  notes: '',
  signature: {
    signatureValue: '',
    partitionkey: '',
    rowkey: '',
    name: null,
    date: null,
  },
  concerns: [
    {
      concernId: null,
      areaOfConcern: null,
      teamMember: null,
      comments: null,
    },
  ],
  id: '',
});

interface UnsavedChangesWatcherProps {
  values: ClinicalEligibilty;
  clinicalJudgementInitialValue: ClinicalEligibilty;
  selectedTeam: unknown;
  onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
}

const UnsavedChangesWatcher = ({
  values,
  clinicalJudgementInitialValue,
  selectedTeam,
  onUnsavedChangesChange,
}: UnsavedChangesWatcherProps) => {
  useEffect(() => {
    if (
      _.isEqual(clinicalJudgementInitialValue, values) &&
      selectedTeam === null
    ) {
      onUnsavedChangesChange(false);
    } else {
      onUnsavedChangesChange(true);
    }
  }, [JSON.stringify(values), selectedTeam]);

  return null;
};

const ClinicalJudgementEligibility = () => {
  const rctl = useIntl();
  const location = useLocation();
  const [clinicalJudgementInitialValue, setClinicalJudgementInitialValue] =
    useState<ClinicalEligibilty>(initialValue);
  const [uploadedToTBR, setUploadedToTBR] = useState<boolean>(false);
  const [numOfConcerns, setNumOfConcerns] = useState<number>(1);
  const [clientId, setClientId] = useState<string>();
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const { hasUnsavedChanges, handleUpdateUnsavedChanges } = useContext(
    UnSavedChangesContext,
  );
  const [successOrError, setSuccessOrError] = useState<AlertColor>('success');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState<string>();
  const [employees, setEmployees] = useState<EmployeeListType[]>([]);
  const [teamMembersEmployees, setTeamMembersEmployees] = useState<
    EmployeeListType[]
  >([]);
  const [selectedTeam, setSelectedTeam] = useState<EmployeeListType | null>(
    null,
  );
  const [selectedTeamDisciplineList, setSelectedTeamDisciplineList] = useState<
    OptionType[]
  >([]);

  const [selectedTeamDiscipline, setSelectedTeamDiscipline] =
    useState<OptionType | null>(null);
  const [signClicked, setSignClicked] = useState<boolean>(false);
  const formikRef = useRef<FormikProps<ClinicalEligibilty>>(null);
  const navigate = useNavigate();
  const [clinicalJudgement, setClinicalJudgement] =
    useState<ClinicalJudgement>();
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const [openUnSavedModal, setOpenUnSavedModal] = useState<boolean>(false);
  const [addMoreToolCount, setAddMoreToolCount] = useState<number>(1);
  const [additionalToolEmployees, setAdditionalToolEmployees] = useState<
    EmployeeListType[]
  >([]);
  const [allDisciplines, setAllDisciplines] = useState<OptionType[]>([]);
  const { lookups } = useContext(LookUpContext);
  const permissions = useMultiplePermissions([
    'backend.clients',
    'backend.in_active_referrals',
  ]);
  const referralStatus = localStorage.getItem(REFERRAL_STATUS_KEY) ?? 'None';
  const hasEditPermission =
    referralStatus === 'Inactive'
      ? permissions['backend.clients'].canEdit &&
        permissions['backend.in_active_referrals'].canEdit
      : permissions['backend.clients'].canEdit ||
        permissions['backend.in_active_referrals'].canEdit;

  useEffect(() => {
    setUploadedToTBR(location.state?.isUploadedToTBR);
    const id = location.state?.evaluationId;
    if (id) {
      setClientId(id);
      getClinicalJudgementDetails(id);
    }
  }, []);

  const hasEvalAdd = checkPermissionForFeature(
    'backend.evaluations',
    'addPermission',
  );
  const hasEvalEdit = checkPermissionForFeature(
    'backend.evaluations',
    'editPermission',
  );
  const hasId = !!clinicalJudgementInitialValue.id;

  const isScreenLocked = useMemo(() => {
    return (
      !hasEditPermission || (!hasId && !hasEvalAdd) || (hasId && !hasEvalEdit)
    );
  }, [hasEditPermission, hasEvalAdd, hasEvalEdit, hasId]);

  const getEmployeeOption = (administeredBy: string) => {
    const emp = additionalToolEmployees.find((e) => e.id === administeredBy);
    return emp
      ? { id: emp.id, label: `${emp.lastName}, ${emp.firstName}` }
      : null;
  };

  useEffect(() => {
    if (isScreenLocked) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isScreenLocked]);

  useEffect(() => {
    if (lookups) {
      setAllDisciplines(
        lookups.employeeDiscipline.map((d) => ({
          label: d.description,
          id: d.code,
        })),
      );
    }
  }, [lookups]);

  const parentRef = useRef<HTMLDivElement>(null);

  const sanitizeSearch = (text: string) => {
    if (!text) return '';

    if (text.includes(',')) {
      const [last, first] = text.split(',');
      return `${first?.trim()} ${last?.trim()}`;
    }

    return text;
  };

  const fetchTeamMemberEmployees = (
    search: string,
    page: number,
  ): Promise<OptionType[]> => {
    const cleanSearch = search ? sanitizeSearch(search) : '';
    return getAllEmployeeByName(cleanSearch, page, 10, [] as string[])
      .then((data) => {
        setTeamMembersEmployees((prev) =>
          page === 0 ? data : [...prev, ...data],
        );
        toggleLoader(false);

        return data.map((emp) => ({
          id: emp.id,
          label: `${emp.lastName}, ${emp.firstName}`,
        }));
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
          return [];
        } else {
          setToastrId('loadFailureMessage');
          setToastrDefaultMessage('Failed to get details');
          return [];
        }
      });
  };

  const fetchAdditionalToolsEmployees = (
    search: string,
    page: number,
    empIds?: string[],
  ): Promise<OptionType[]> => {
    const cleanSearch = search ? sanitizeSearch(search) : '';
    return getAllEmployeeByName(
      cleanSearch,
      page,
      10,
      page === 0 ? empIds ?? [] : [],
    )
      .then((data) => {
        toggleLoader(false);
        setAdditionalToolEmployees((prev) => {
          const map = new Map(prev.map((emp) => [emp.id, emp]));
          data.forEach((emp) => map.set(emp.id, emp));
          return Array.from(map.values());
        });

        return data.map((emp) => ({
          id: emp.id,
          label: `${emp.lastName}, ${emp.firstName}`,
        }));
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
          return [];
        } else {
          setToastrId('loadFailureMessage');
          setToastrDefaultMessage('Failed to get details');
          return [];
        }
      });
  };

  const getClinicalJudgementDetails = (cId: string) => {
    toggleLoader(true);
    getClientClinicalJudgementDetails(cId)
      .then((response) => {
        const preValues = cloneDeep(formikRef.current?.values);
        if (preValues) {
          preValues.concerns =
            response.concerns.length > 0
              ? response.concerns
              : [
                  {
                    concernId: null,
                    areaOfConcern: null,
                    teamMember: null,
                    comments: null,
                  },
                ];
          preValues.signature = response.signature;
          preValues.teams = response.teams;
          if (response.additionalTools) {
            if (response.additionalTools.tools.length === 0) {
              preValues.additionalTools = {
                tools: [
                  {
                    additionalToolsId: '',
                    nameOfToolUsed: null,
                    dateAdministered: null,
                    administeredBy: null,
                    disciplineOfPerson: null,
                    specify: null,
                    status: null,
                  },
                ],
                additionalToolComments:
                  response.additionalTools.additionalToolComments,
              };
            } else {
              preValues.additionalTools = response.additionalTools;
            }
          }
          preValues.notes = response.notes;
          preValues.id = response.id;
          formikRef.current?.setValues(preValues);
          setAddMoreToolCount(preValues.additionalTools?.tools.length || 1);
          setClinicalJudgementInitialValue(cloneDeep(preValues));
          setNumOfConcerns(
            response.concerns.length > 0 ? response.concerns.length : 1,
          );
          setClinicalJudgement(response);
          const additionalToolEmpIds =
            response.additionalTools?.tools?.map(
              (item) => item.administeredBy,
            ) ?? [];

          const inventoryConcernsEmpIds =
            response.concerns?.map((item) => item.teamMember) ?? [];

          // Remove duplicates

          const allUniqueConcernEmpIds = [
            ...new Set(inventoryConcernsEmpIds),
          ] as string[];

          const allUniqueToolEmpIds = [
            ...new Set(additionalToolEmpIds),
          ] as string[];

          getEmployeesData(allUniqueConcernEmpIds);

          fetchAdditionalToolsEmployees('', 0, allUniqueToolEmpIds);
        }
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('loadFailureMessage');
          setToastrDefaultMessage('Failed to get details');
        }
      });
  };
  const deleteTeamMember = (id: string) => {
    toggleLoader(true);
    deleteTeamMemberById(id)
      .then(() => {
        const values = formikRef.current?.values;
        if (values && values.teams !== null && values.teams.length > 0) {
          formikRef.current?.setValues({
            ...values,
            teams: values.teams.filter((item) => item.teamsId !== id),
          });
        }
        toggleLoader(false);
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          const errorMessage = error as ApiMessage;
          if (errorMessage.code === 1026) {
            setToastrId('judgementConflictFailureMessage');
            setToastrDefaultMessage(
              'Deleting team is not allowed if only one team is available',
            );
          } else {
            setToastrId('Contacts.deleteToastr');
            setToastrDefaultMessage('Failed to delete');
          }
        }
      });
  };

  const formatDisciplineType = (disciplineType: string) => {
    if (lookups === null) return '';
    const discipline = lookups.employeeDiscipline.find((d) => {
      if (d.code === disciplineType) {
        return d;
      }
    });
    if (discipline) {
      return discipline.description;
    }
    return '';
  };

  const deleteConcern = (id: string) => {
    toggleLoader(true);
    deleteConcernById(id)
      .then(() => {
        const values = formikRef.current?.values;
        if (values && values.concerns !== null && values.concerns.length > 0) {
          if (numOfConcerns - 1 === 0) {
            formikRef.current?.setFieldValue('concerns', [
              {
                concernId: null,
                areaOfConcern: null,
                teamMember: null,
                comments: null,
              },
            ]);
            setNumOfConcerns(1);
          } else {
            formikRef.current?.setValues({
              ...values,
              concerns: values.concerns.filter((item) => item.concernId !== id),
            });
            setNumOfConcerns(numOfConcerns - 1);
          }
        }
        toggleLoader(false);
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          const errorMessage = error as ApiMessage;
          if (errorMessage.code === 1025) {
            setToastrId('judgementConcernFailureMessage');
            setToastrDefaultMessage(
              'Deleting concern is not allowed if only one concern is available',
            );
          } else {
            setToastrId('Contacts.deleteToastr');
            setToastrDefaultMessage('Failed to delete');
          }
        }
      });
  };

  const getEmployeesData = (allUniqueEmpIds: string[]) => {
    getAllEmployeeMinimalList(allUniqueEmpIds)
      .then((data) => {
        toggleLoader(false);
        setEmployees(data);
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('loadFailureMessage');
          setToastrDefaultMessage('Failed to get details');
        }
      });
  };

  const processAndSubmitResponse = (value: ClinicalJudgement) => {
    value.concerns = value.concerns.filter(
      (item) =>
        item.areaOfConcern !== null ||
        item.teamMember !== null ||
        item.comments !== null,
    );
    if (value.additionalTools) {
      value.additionalTools.tools = value.additionalTools?.tools.filter(
        (item) =>
          item.nameOfToolUsed !== null ||
          item.dateAdministered !== null ||
          item.administeredBy !== null ||
          item.disciplineOfPerson !== null,
      );
    }
    if (clientId === undefined) {
      return;
    }
    toggleLoader(true);
    saveClinicalJudgement(clientId, value)
      .then(() => {
        getClinicalJudgementDetails(clientId);
        setSuccessOrError('success');
        if (value.id) {
          setToastrId('clinicalJudgementUpdated');
          setToastrDefaultMessage('Clinical judgement updated successfully');
        } else {
          setToastrId('clinicalJudgementSaved');
          setToastrDefaultMessage('Clinical judgement saved successfully');
        }
      })
      .catch((error) => {
        toggleLoader(false);
        setSuccessOrError('error');
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrId('clinicalJudgementFailedToUpdate');
          setToastrDefaultMessage('Failed to update clinical judgement');
        }
      });
  };

  const getBDIValues = () => {
    const bdiValues: DataJsonType[] = [];
    if (clinicalJudgement) {
      bdiValues.push({
        id: 'toolsUsed',
        labelid: 'toolsUsedText',
        defaultLabel: 'Tools Used:',
        value: 'Battelle Development Inventory',
      });
    }
    return bdiValues;
  };

  const onSignatureSaveOrUpdate = (signed: string) => {
    const formatedUserName = formatName(
      localStorage.getItem('userFirstName'),
      null,
      localStorage.getItem('userLastName'),
      null,
    );
    if (clinicalJudgement) {
      setClinicalJudgement({
        ...clinicalJudgement,
        signature: {
          partitionkey: '',
          rowkey: '',
          name: formatedUserName,
          date: dayjs.utc().format(),
          signatureValue: signed,
        },
      });
    }
    if (formikRef.current) {
      const values = formikRef.current.values;
      values.signature = {
        ...values.signature,
        partitionkey: '',
        rowkey: '',
        signatureValue: signed,
        name: formatedUserName,
        date: dayjs.utc().format(),
      };
      formikRef.current?.setValues(values);
    }
    setSignClicked(false);
  };

  const onCancelClick = () => {
    setSignClicked(false);
  };

  const handleSuccess = (successError: AlertColor, id: string, mes: string) => {
    setSuccessOrError(successError);
    setToastrId(id);
    setToastrDefaultMessage(mes);
  };

  const resetCallBackForTypeEmployee = () => {
    setSignClicked(!signClicked);

    formikRef.current?.setValues((values) => ({
      ...values,
      signature: {
        partitionkey: '',
        rowkey: '',
        signatureValue: '',
        isUpdated: true,
        name: null,
        date: null,
      },
    }));
  };

  const handleFormSubmission = useCallback(
    debounce((values: ClinicalJudgement) => {
      processAndSubmitResponse(values);
    }, 500),
    [clientId],
  );
  return (
    <Box component="main">
      <Toolbar disableGutters>
        <Box
          display="flex"
          alignItems="center"
          className="mr-md"
          sx={{ cursor: 'pointer' }}
          onClick={() => {
            if (hasUnsavedChanges) {
              setOpenUnSavedModal(true);
            } else {
              handleUpdateUnsavedChanges(false);
              navigate(BATELLE_DEVELOPMENT_INVENTORY_2);
            }
          }}
        >
          <Backarrow />
        </Box>
        <TitleText
          labelid="clinicalJudgementText"
          defaultlabel="Clinical Judgement"
        />
      </Toolbar>
      <Box
        ref={parentRef}
        style={{
          pointerEvents: isScreenLocked || uploadedToTBR ? 'none' : 'auto',
          opacity: isScreenLocked || uploadedToTBR ? '0.8' : 'unset',
        }}
      >
        <Formik
          innerRef={formikRef}
          initialValues={initialValue}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleFormSubmission(structuredClone(values) as ClinicalJudgement);
          }}
          enableReinitialize
          validateOnMount
          validateOnBlur
        >
          {({ values, setFieldValue, errors, touched }) => {
            return (
              <>
                <UnsavedChangesWatcher
                  values={values}
                  clinicalJudgementInitialValue={clinicalJudgementInitialValue}
                  selectedTeam={selectedTeam}
                  onUnsavedChangesChange={handleUpdateUnsavedChanges}
                />
                {showCancelDialog && (
                  <ModalPopup
                    open={showCancelDialog}
                    maxWidth={'xs'}
                    description="formUnsavedChangesMessage"
                    onCancel={() => setShowCancelDialog(false)}
                    onOk={() => {
                      formikRef.current?.setValues(
                        clinicalJudgementInitialValue,
                      );
                      setShowCancelDialog(false);
                    }}
                    labelId1="Clientpage.Nobtn"
                    negativeActionLabel="No"
                    labelId2="Clientpage.Yesbtn"
                    positiveActionLabel="Yes"
                  />
                )}
                <ModalPopup
                  open={openUnSavedModal}
                  description="UnsavedAlertDescriptionWhileLeaving"
                  labelId1="UnSavedAlertCancel"
                  labelId2="UnSavedAlertLeave"
                  negativeActionLabel="No"
                  positiveActionLabel="Yes"
                  onCancel={() => {
                    setOpenUnSavedModal(false);
                  }}
                  onOk={() => {
                    navigate(BATELLE_DEVELOPMENT_INVENTORY_2);
                    handleUpdateUnsavedChanges(false);
                    setOpenUnSavedModal(false);
                  }}
                />
                {toastrId && (
                  <SnackBarComponent
                    open={toastrId !== undefined}
                    handleClose={() => {
                      setToastrId(undefined);
                    }}
                    labelId={toastrId}
                    defaultMessageId={toastrDefaultMessage}
                    successOrError={successOrError}
                  />
                )}

                <>
                  {clinicalJudgement && (
                    <Box component="section">
                      <Card sx={{ padding: '16px' }}>
                        <ProgramPanel
                          program={clinicalJudgement.program}
                          client={clinicalJudgement.client}
                        />
                      </Card>
                    </Box>
                  )}

                  <Box component="section" className="pt-md pb-md">
                    <Card className="p-lg">
                      <Box>
                        <MediumTypography
                          labelid="scoresText"
                          defaultlabel="Scores"
                          className="mainText-xlg pb-md"
                        />
                        <KeyValueComponent data={getBDIValues()} />
                        {clinicalJudgement &&
                          clinicalJudgement.bdiVersion === 'BDI2' && (
                            <>
                              <Grid container columnSpacing={'20px'}>
                                <Grid item xs={3.5} className="pt-md">
                                  <CustomcardComponent
                                    headTextWithValue={`Adaptive(DQ):`}
                                    headTextWithValue2={
                                      clinicalJudgement
                                        .developmentQuotientResults
                                        .adaptiveDQScore
                                    }
                                    subText1={`Self Care(ScRaw):`}
                                    subText1Value={
                                      clinicalJudgement.subDomainsResults
                                        .selfCareScore
                                    }
                                    subText2={`Personal responsibility (PrRaw):`}
                                    subText2Value={
                                      clinicalJudgement.subDomainsResults
                                        .personalResponsibilityScore
                                    }
                                  />
                                </Grid>

                                <Grid item xs={3.5} className="pt-md">
                                  <CustomcardComponent
                                    headTextWithValue={`Social-Emotional(DQ):`}
                                    headTextWithValue2={
                                      clinicalJudgement
                                        .developmentQuotientResults
                                        .personalSocialDQScore
                                    }
                                    subText1={`Adult Interaction(AiRaw):`}
                                    subText1Value={
                                      clinicalJudgement.subDomainsResults
                                        .adultInteractionScore
                                    }
                                    subText2={`Peer Interaction(PiRaw):`}
                                    subText2Value={
                                      clinicalJudgement.subDomainsResults
                                        .peerInteractionScore
                                    }
                                    subText3={`Self Concept & Social Role (SrRaw):`}
                                    subText3Value={
                                      clinicalJudgement.subDomainsResults
                                        .selfConceptSocialRoleScore
                                    }
                                  />
                                </Grid>
                                <Grid item xs={3.5} className="pt-md">
                                  <CustomcardComponent
                                    headTextWithValue={`Communication(DQ):`}
                                    headTextWithValue2={
                                      clinicalJudgement
                                        .developmentQuotientResults
                                        .communicationDQScore
                                    }
                                    subText1={`Receptive Communication (RcRaw):`}
                                    subText1Value={
                                      clinicalJudgement.subDomainsResults
                                        .respectiveCommunicationScore
                                    }
                                    subText2={`Expressive Communication (EcRaw):`}
                                    subText2Value={
                                      clinicalJudgement.subDomainsResults
                                        .expressiveCommunicationScore
                                    }
                                  />
                                </Grid>
                              </Grid>
                              <Grid container columnSpacing={'20px'}>
                                <Grid item xs={3.5} className="pt-md">
                                  <CustomcardComponent
                                    headTextWithValue={`Motor(DQ):`}
                                    headTextWithValue2={
                                      clinicalJudgement
                                        .developmentQuotientResults.motorDQScore
                                    }
                                    subText1={`Gross Motor(GmRaw):`}
                                    subText1Value={
                                      clinicalJudgement.subDomainsResults
                                        .grossMotorScore
                                    }
                                    subText2={`Fine Motor(FmRaw):`}
                                    subText2Value={
                                      clinicalJudgement.subDomainsResults
                                        .fineMotorScore
                                    }
                                    subText3={`Perceptual Motor(PmRaw):`}
                                    subText3Value={
                                      clinicalJudgement.subDomainsResults
                                        .perceptualMotorScore
                                    }
                                  />
                                </Grid>
                                <Grid item xs={3.5} className="pt-md">
                                  <CustomcardComponent
                                    headTextWithValue={`Cognitive(DQ):`}
                                    headTextWithValue2={
                                      clinicalJudgement
                                        .developmentQuotientResults
                                        .cognitiveDQScore
                                    }
                                    subText1={`Attention / Memory (AmRaw): `}
                                    subText1Value={
                                      clinicalJudgement.subDomainsResults
                                        .attentionMemoryScore
                                    }
                                    subText2={`Reasoning / Academic Skills (RaRaw):`}
                                    subText2Value={
                                      clinicalJudgement.subDomainsResults
                                        .reasoningAcademicSkillsScore
                                    }
                                    subText3={`Perception / Concepts (PcRaw):`}
                                    subText3Value={
                                      clinicalJudgement.subDomainsResults
                                        .perceptionConceptsScore
                                    }
                                  />
                                </Grid>
                              </Grid>
                            </>
                          )}
                        {clinicalJudgement &&
                          clinicalJudgement.bdiVersion === 'BDI3' && (
                            <>
                              <Grid container columnSpacing={'20px'}>
                                <Grid item xs={3.5} className="pt-md">
                                  <CustomcardComponent
                                    headTextWithValue={`Adaptive(DQ):`}
                                    headTextWithValue2={
                                      clinicalJudgement
                                        .developmentQuotientResults
                                        .adaptiveDQScore
                                    }
                                    onlyHeadText={true}
                                  />
                                </Grid>

                                <Grid item xs={3.5} className="pt-md">
                                  <CustomcardComponent
                                    headTextWithValue={`Social-Emotional(DQ):`}
                                    headTextWithValue2={
                                      clinicalJudgement
                                        .developmentQuotientResults
                                        .personalSocialDQScore
                                    }
                                    onlyHeadText={true}
                                  />
                                </Grid>
                                <Grid item xs={3.5} className="pt-md">
                                  <CustomcardComponent
                                    headTextWithValue={`Communication(DQ):`}
                                    headTextWithValue2={
                                      clinicalJudgement
                                        .developmentQuotientResults
                                        .communicationDQScore
                                    }
                                    onlyHeadText={true}
                                  />
                                </Grid>
                              </Grid>
                              <Grid container columnSpacing={'20px'}>
                                <Grid item xs={3.5} className="pt-md">
                                  <CustomcardComponent
                                    headTextWithValue={`Motor(DQ):`}
                                    headTextWithValue2={
                                      clinicalJudgement
                                        .developmentQuotientResults.motorDQScore
                                    }
                                    onlyHeadText={true}
                                  />
                                </Grid>
                                <Grid item xs={3.5} className="pt-md">
                                  <CustomcardComponent
                                    headTextWithValue={`Cognitive(DQ):`}
                                    headTextWithValue2={
                                      clinicalJudgement
                                        .developmentQuotientResults
                                        .cognitiveDQScore
                                    }
                                    onlyHeadText={true}
                                  />
                                </Grid>
                              </Grid>
                            </>
                          )}
                        <Box className="pt-xlg">
                          <MediumTypography
                            labelid="disciplinesNote"
                            defaultlabel="Disciplines on Team:"
                            className="mainText-md"
                          />

                          <Box component="div">
                            <Box component="div" className="mt-md">
                              <Grid container columnSpacing={'40px'}>
                                <Grid item xs={5}>
                                  <LazyAutocompleteSearch
                                    labelId="IFSP.selectName"
                                    defaultId="Select Name"
                                    value={
                                      selectedTeam
                                        ? {
                                            id: selectedTeam.id,
                                            label: `${selectedTeam.lastName}, ${selectedTeam.firstName}`,
                                          }
                                        : null
                                    }
                                    onChange={(value) => {
                                      if (value) {
                                        const employee =
                                          teamMembersEmployees.find(
                                            (item) => item.id === value?.id,
                                          );

                                        setSelectedTeam(() =>
                                          employee
                                            ? {
                                                ...employee,
                                                id: value?.id ?? '',
                                                disciplineType:
                                                  selectedTeamDiscipline?.id ??
                                                  '',
                                              }
                                            : null,
                                        );

                                        setSelectedTeamDisciplineList(() => {
                                          if (
                                            !teamMembersEmployees ||
                                            !lookups?.employeeDiscipline
                                          )
                                            return [];

                                          const disciplineMap = new Map<
                                            string,
                                            { id: string; label: string }
                                          >();

                                          teamMembersEmployees
                                            .filter(
                                              (contact) =>
                                                contact.id === value.id,
                                            )
                                            .forEach((contact) => {
                                              (
                                                contact.disciplineHistoryList ||
                                                []
                                              ).forEach((history) => {
                                                const discipline =
                                                  lookups.employeeDiscipline.find(
                                                    (dis) =>
                                                      dis.code ===
                                                      history.disciplineType,
                                                  );

                                                if (discipline?.code) {
                                                  disciplineMap.set(
                                                    discipline.code,
                                                    {
                                                      id: discipline.code,
                                                      label:
                                                        discipline.description,
                                                    },
                                                  );
                                                }
                                              });
                                            });

                                          return Array.from(
                                            disciplineMap.values(),
                                          );
                                        });
                                      } else {
                                        setSelectedTeam(null);
                                        setSelectedTeamDisciplineList([]);
                                        setSelectedTeamDiscipline(null);
                                      }
                                    }}
                                    fetchOptions={fetchTeamMemberEmployees}
                                    required
                                    disabled={
                                      uploadedToTBR || !hasEditPermission
                                    }
                                  />
                                </Grid>
                                <Grid item xs={5} display={'none'}>
                                  <DropdownComponent
                                    disabled={
                                      uploadedToTBR || !hasEditPermission
                                    }
                                    names={employees
                                      .filter((emp) => !emp.isInActive)
                                      .map((item) => {
                                        return {
                                          label: `${item.lastName ?? ''}, ${
                                            item.firstName ?? ''
                                          }`,
                                          id: item.id,
                                        };
                                      })}
                                    labelid="IFSP.selectName"
                                    defaultlabelid="Select Name"
                                    value={
                                      selectedTeam !== null
                                        ? selectedTeam.id
                                        : ''
                                    }
                                    handleChange={(value) => {
                                      const employee = employees.find(
                                        (item) => item.id === value,
                                      );
                                      setSelectedTeam(employee ?? null);
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={5} className="mb-md">
                                  <DropdownComponent
                                    disabled={
                                      uploadedToTBR || !hasEditPermission
                                    }
                                    names={selectedTeamDisciplineList}
                                    labelid="selectDisciplineText"
                                    defaultlabelid="Select Discipline"
                                    value={
                                      selectedTeamDiscipline
                                        ? selectedTeamDiscipline?.id
                                        : ''
                                    }
                                    handleChange={(value) => {
                                      if (value) {
                                        const discipline =
                                          selectedTeamDisciplineList.find(
                                            (item) => item.id === value,
                                          );
                                        setSelectedTeamDiscipline(
                                          discipline ?? {
                                            id: '',
                                            label: '',
                                          },
                                        );
                                        setSelectedTeam((prev) =>
                                          prev
                                            ? {
                                                ...prev,
                                                disciplineType:
                                                  discipline?.id ?? '',
                                              }
                                            : prev,
                                        );
                                      } else {
                                        setSelectedTeamDiscipline(null);
                                        setSelectedTeam((prev) =>
                                          prev
                                            ? { ...prev, disciplineType: '' }
                                            : prev,
                                        );
                                      }
                                    }}
                                    Required
                                  />
                                  {!selectedTeamDiscipline &&
                                    selectedTeam?.id && (
                                      <MediumTypography
                                        className="errorText-md"
                                        labelid="IFSP.yetToAddDisciplineId.validation"
                                        defaultlabel="Discipline is required"
                                      />
                                    )}
                                </Grid>
                                <Grid
                                  container
                                  xs={1}
                                  className="cursorPointer "
                                  onClick={() => {
                                    if (
                                      selectedTeam !== null &&
                                      values.teams !== null
                                    ) {
                                      if (!selectedTeamDiscipline) {
                                        return;
                                      }
                                      const existingTeams = values.teams;
                                      if (existingTeams.length > 0) {
                                        const emp = existingTeams.find(
                                          (team) =>
                                            team.email === selectedTeam.email &&
                                            team.disciplineType ===
                                              selectedTeam.disciplineType,
                                        );
                                        if (emp) {
                                          return;
                                        }
                                      }
                                      existingTeams.push({
                                        teamsId: '',
                                        name: `${selectedTeam.firstName} ${selectedTeam.lastName}`,
                                        disciplineType:
                                          selectedTeam.disciplineType !== null
                                            ? selectedTeam.disciplineType
                                            : '-',
                                        email:
                                          selectedTeam.email !== null
                                            ? selectedTeam.email
                                            : '-',
                                        phone:
                                          selectedTeam.phone !== null
                                            ? selectedTeam.phone
                                            : '-',
                                      });
                                      formikRef.current?.setValues({
                                        ...values,
                                        teams: existingTeams,
                                      });
                                      setSelectedTeam(null);
                                      setSelectedTeamDiscipline(null);
                                      setSelectedTeamDisciplineList([]);
                                    }
                                  }}
                                >
                                  <Grid item>
                                    <Box
                                      component="div"
                                      className="flex__ pt-xs "
                                    >
                                      <AddNew className="ml-sm mr-sm" />
                                      <MediumTypography
                                        labelid="IFSP.Add"
                                        defaultlabel="Add"
                                      />
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Box>
                            <TableContainer>
                              <Table>
                                <TableHeader
                                  className="listDataTableHead"
                                  headerNames={teamHeadCells}
                                  checkBoxRequired={false}
                                />
                                <TableBody className="tableRowcss">
                                  {values.teams !== null &&
                                    values.teams.map((team, index) => (
                                      <TableRow
                                        key={`team-${index + 1}`}
                                        sx={{
                                          backgroundColor:
                                            index % 2 === 0
                                              ? '#ECF9F8'
                                              : '#ffffff',
                                        }}
                                      >
                                        <TableCell>
                                          <MediumTypography
                                            label={team.name
                                              .split(' ')
                                              .reverse()
                                              .join(', ')}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <MediumTypography
                                            label={formatDisciplineType(
                                              team.disciplineType,
                                            )}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <MediumTypography
                                            label={team.email}
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <MediumTypography
                                            label={team.phone}
                                          />
                                        </TableCell>
                                        <TableCell
                                          className="pt-md"
                                          sx={{ cursor: 'pointer' }}
                                        >
                                          <ActionToolTip
                                            labelId="BlockTime.delete"
                                            defaultLabel="Delete"
                                          >
                                            <DeleteIcon
                                              onClick={() => {
                                                if (values.teams !== null) {
                                                  const teamMember =
                                                    values.teams[index];
                                                  if (
                                                    teamMember.teamsId === ''
                                                  ) {
                                                    formikRef.current?.setValues(
                                                      {
                                                        ...values,
                                                        teams:
                                                          values.teams.filter(
                                                            (item) =>
                                                              item.email !==
                                                              teamMember.email,
                                                          ),
                                                      },
                                                    );
                                                  } else {
                                                    deleteTeamMember(
                                                      teamMember.teamsId,
                                                    );
                                                  }
                                                }
                                              }}
                                            />
                                          </ActionToolTip>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                </TableBody>
                              </Table>
                              {values.teams !== null &&
                                values.teams.length === 0 && (
                                  <Box component="div" className="width__100">
                                    <MediumTypography
                                      textstyle={'center'}
                                      labelid="IFSP.emptyMessage"
                                      defaultlabel="No data added yet"
                                      className="p-sm"
                                    />
                                  </Box>
                                )}
                            </TableContainer>
                          </Box>
                        </Box>

                        <Box className="mt-sm pt-xlg">
                          <MediumTypography
                            labelid="assessmentNote"
                            defaultlabel="Additional Assessment Tool(s) Used: (Note: An additional assessment tool specific to the area of concern is required to be administered)."
                            className="mainText-md"
                          />

                          {values.additionalTools?.tools.map((item, index) => (
                            <Box key={`assessmentTool-${index + 1}`}>
                              {(item.status === null || item.status === '') && (
                                <Box sx={{ display: 'flex' }}>
                                  <Grid
                                    container
                                    xs={8}
                                    className="pt-md"
                                    columnSpacing={'32px'}
                                    rowSpacing={'24px'}
                                  >
                                    {index !== 0 && (
                                      <Grid item xs={12}>
                                        <Box className="defaultHorizontalLine" />
                                      </Grid>
                                    )}

                                    <Grid item xs={6}>
                                      {clinicalJudgement && (
                                        <SelectComponent
                                          disabled={uploadedToTBR}
                                          names={clinicalJudgement.assessmentTools.map(
                                            (tool) => tool.nameOfTool,
                                          )}
                                          labelid="nameOfToolsText"
                                          defaultValue="Name of Tool(s)"
                                          value={item.nameOfToolUsed ?? ''}
                                          handleChange={(value) => {
                                            const toolFound =
                                              clinicalJudgement.assessmentTools.find(
                                                (tool) =>
                                                  tool.nameOfTool === value,
                                              );
                                            if (toolFound) {
                                              const date =
                                                toolFound.dateAdministered !==
                                                null
                                                  ? dayjs(
                                                      toolFound.dateAdministered,
                                                    ).format('MM/DD/YYYY')
                                                  : null;
                                              if (
                                                values.additionalTools !== null
                                              ) {
                                                const tool =
                                                  values.additionalTools.tools[
                                                    index
                                                  ];
                                                tool.nameOfToolUsed = value;
                                                tool.dateAdministered = date;
                                                formikRef.current?.setValues(
                                                  values,
                                                );
                                              }
                                            }
                                          }}
                                        />
                                      )}
                                    </Grid>

                                    <Grid item xs={6}>
                                      <DatePickerComponent
                                        disabledDate={uploadedToTBR}
                                        labelid="dateAdministeredText"
                                        defaultlabelid="Date Administered"
                                        value={
                                          item.dateAdministered
                                            ? dayjs(item.dateAdministered)
                                            : null
                                        }
                                        handlechange={(date) => {
                                          const formattedDate =
                                            dayjs(date).format('MM/DD/YYYY');
                                          if (values.additionalTools) {
                                            values.additionalTools.tools[
                                              index
                                            ].dateAdministered =
                                              date !== null
                                                ? formattedDate
                                                : null;
                                          }
                                          formikRef.current?.setValues(values);
                                        }}
                                      />
                                      {datePickerMinDateMaxDateValidate(
                                        item.dateAdministered,
                                      ) && (
                                        <MediumTypography
                                          className="errorText-md"
                                          labelid={
                                            'datePickerMinDateMaxDateValidate'
                                          }
                                          defaultlabel="Enter a date between 01/01/1900 and 01/01/2100
"
                                        />
                                      )}
                                    </Grid>

                                    {item.nameOfToolUsed !== null &&
                                      item.nameOfToolUsed === 'Other' && (
                                        <Grid item xs={12}>
                                          <Textinput
                                            disabled={uploadedToTBR}
                                            inputProps={{ maxLength: 50 }}
                                            Value={item.specify ?? ''}
                                            labelid="IFSP.specify"
                                            defaultlabelid="Specify"
                                            handlechange={(value) => {
                                              if (values.additionalTools) {
                                                values.additionalTools.tools[
                                                  index
                                                ].specify = value;
                                              }
                                              formikRef.current?.setValues(
                                                values,
                                              );
                                            }}
                                          />
                                        </Grid>
                                      )}

                                    <Grid item xs={6}>
                                      <Box>
                                        <LazyAutocompleteSearch
                                          labelId="administeredByText"
                                          defaultId="Administered By"
                                          value={getEmployeeOption(
                                            item.administeredBy ?? '',
                                          )}
                                          onChange={(value) => {
                                            if (value) {
                                              if (
                                                values.additionalTools !== null
                                              ) {
                                                const tool =
                                                  values.additionalTools.tools[
                                                    index
                                                  ];
                                                tool.administeredBy = value.id;
                                                formikRef.current?.setValues(
                                                  values,
                                                );
                                              }
                                            } else if (
                                              values.additionalTools !== null
                                            ) {
                                              const tool =
                                                values.additionalTools.tools[
                                                  index
                                                ];

                                              tool.administeredBy = null;
                                              tool.disciplineOfPerson = '';

                                              formikRef.current?.setValues(
                                                values,
                                              );
                                            }
                                          }}
                                          fetchOptions={
                                            fetchAdditionalToolsEmployees
                                          }
                                          disabled={
                                            uploadedToTBR || !hasEditPermission
                                          }
                                        />
                                        <MediumTypography />
                                      </Box>
                                    </Grid>

                                    <Grid item xs={6}>
                                      <DropdownComponent
                                        disabled={
                                          uploadedToTBR || !hasEditPermission
                                        }
                                        names={(() => {
                                          const emp =
                                            additionalToolEmployees.find(
                                              (e) =>
                                                e.id === item.administeredBy,
                                            );

                                          if (!emp?.disciplineHistoryList)
                                            return [];

                                          const map = new Map<
                                            string,
                                            OptionType
                                          >();

                                          emp.disciplineHistoryList.forEach(
                                            (history) => {
                                              const discipline =
                                                allDisciplines.find(
                                                  (d) =>
                                                    d.id ===
                                                    history.disciplineType,
                                                );

                                              if (discipline) {
                                                map.set(discipline.id, {
                                                  id: discipline.id,
                                                  label: discipline.label,
                                                });
                                              }
                                            },
                                          );

                                          return Array.from(map.values());
                                        })()}
                                        labelid="disciplineAdministeringText"
                                        defaultlabelid="Discipline of Person Administering"
                                        value={
                                          item.disciplineOfPerson !== null &&
                                          item.disciplineOfPerson !== ''
                                            ? item.disciplineOfPerson.split(
                                                ',',
                                              )[0]
                                            : ''
                                        }
                                        handleChange={(value) => {
                                          if (value) {
                                            if (
                                              values.additionalTools !== null
                                            ) {
                                              const tool =
                                                values.additionalTools.tools[
                                                  index
                                                ];
                                              tool.disciplineOfPerson =
                                                allDisciplines.find(
                                                  (dis) => dis.id === value,
                                                )?.id ?? '';
                                              formikRef.current?.setValues(
                                                values,
                                              );
                                            }
                                          } else if (
                                            values.additionalTools !== null
                                          ) {
                                            const tool =
                                              values.additionalTools.tools[
                                                index
                                              ];

                                            tool.disciplineOfPerson = '';

                                            formikRef.current?.setValues(
                                              values,
                                            );
                                          }
                                        }}
                                        Required={Boolean(item.administeredBy)}
                                      />
                                      {getIn(
                                        errors,
                                        `additionalTools.tools[${index}].disciplineOfPerson`,
                                      ) &&
                                        getIn(
                                          touched,
                                          `additionalTools.tools[${index}].disciplineOfPerson`,
                                        ) && (
                                          <MediumTypography
                                            className="errorText-md"
                                            labelid={
                                              'disciplineOfPersonRequired'
                                            }
                                            defaultlabel="Discipline of Person Administering is required"
                                          />
                                        )}
                                    </Grid>
                                  </Grid>

                                  {addMoreToolCount !== 1 && (
                                    <Box
                                      sx={{
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignSelf: 'flex-end',
                                        marginLeft: '16px',
                                        marginBottom: '8px',
                                      }}
                                      onClick={() => {
                                        if (values.additionalTools) {
                                          const t =
                                            values.additionalTools.tools[index];
                                          let tools: Tools[] = [];
                                          if (t.additionalToolsId === '') {
                                            tools =
                                              values.additionalTools.tools.filter(
                                                (__, i) => i !== index,
                                              );
                                            const count = tools.filter(
                                              (i) => i.status !== 'DELETE',
                                            ).length;
                                            setAddMoreToolCount(count);
                                          } else {
                                            tools =
                                              values.additionalTools.tools.map(
                                                (tool, i) => {
                                                  if (index === i) {
                                                    tool.status = 'DELETE';
                                                  }
                                                  return tool;
                                                },
                                              );
                                            const count = tools.filter(
                                              (i) => i.status !== 'DELETE',
                                            ).length;
                                            setAddMoreToolCount(count);
                                          }
                                          formikRef.current?.setValues({
                                            ...values,
                                            additionalTools: {
                                              ...values.additionalTools,
                                              tools,
                                            },
                                          });
                                        }
                                      }}
                                    >
                                      <DeleteAlertIcon />
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          ))}

                          {addMoreToolCount < 3 && (
                            <ButtonComponent
                              disabled={uploadedToTBR}
                              className="btn-add-code mt-md"
                              variantType="contained"
                              type="submit"
                              label={`${rctl.formatMessage({
                                id: 'addMoreToolText',
                                defaultMessage: '+Add More Tools',
                              })}`}
                              defaultLabelId="+Add More Tools"
                              onClick={() => {
                                setAddMoreToolCount(addMoreToolCount + 1);
                                if (values.additionalTools) {
                                  values.additionalTools.tools.push({
                                    additionalToolsId: '',
                                    nameOfToolUsed: null,
                                    dateAdministered: null,
                                    administeredBy: null,
                                    disciplineOfPerson: null,
                                    specify: null,
                                    status: null,
                                  });
                                  formikRef.current?.setValues(values);
                                }
                              }}
                            />
                          )}
                        </Box>

                        <Box
                          sx={{
                            width: '64%',
                            marginTop: addMoreToolCount === 3 ? '16px' : '8px',
                          }}
                        >
                          <CommentTextbox
                            name="additionalTools"
                            disabled={uploadedToTBR}
                            placeholder={rctl.formatMessage({
                              id: 'additionalToolCommentsText',
                              defaultMessage: 'Additional Tool Comments',
                            })}
                            maxLength={1000}
                            rows={2}
                            Value={
                              values.additionalTools !== null
                                ? values.additionalTools.additionalToolComments
                                : ''
                            }
                            handlechange={(text) => {
                              setFieldValue(
                                'additionalTools.additionalToolComments',
                                text,
                              );
                            }}
                          />
                        </Box>

                        <Box className="pt-lg pb-lg">
                          <MediumTypography
                            labelid="JustificationConcernsText"
                            defaultlabel="Justification of Qualitative Concerns"
                            className="mainText-xlg"
                          />
                          <MediumTypography
                            labelid="JustificationDescText"
                            defaultlabel="How do the atypical behaviours / Developmental patterns affect the child’s functioning in the context of the child’s daily activities and/or interactions with peer/adults"
                            className="mainText-md pt-md pb-md"
                          />
                          <Grid container xs={7.6}>
                            <CommentTextbox
                              name="concerns"
                              disabled={uploadedToTBR}
                              placeholder={rctl.formatMessage({
                                id: 'concernsText',
                                defaultMessage: 'Concerns',
                              })}
                              maxLength={3000}
                              rows={5}
                              Value={values.notes}
                              handlechange={(text) => {
                                setFieldValue('notes', text);
                              }}
                              Required
                            />
                            {errors.notes && touched.notes && (
                              <MediumTypography
                                className="errorText-md"
                                labelid={errors.notes as string}
                                defaultlabel="Concerns are Required"
                              />
                            )}
                          </Grid>
                        </Box>

                        <MediumTypography
                          labelid="areaOfConcernsTitleText"
                          defaultlabel="Battelle Developmental Inventory Concerns"
                          className="mainText-xlg mb-lg"
                        />

                        <Box
                          className="p-sm borderRadius4"
                          sx={{
                            backgroundColor: '#ECF9F8',
                            border: '1px solid #00C6B8',
                          }}
                        >
                          {[...Array(numOfConcerns)].map((__, index) => {
                            return (
                              <AreaOfConcern
                                hasEditPermission={hasEditPermission}
                                key={(index + 1).toString()}
                                uploadedToTBR={uploadedToTBR}
                                values={values.concerns}
                                areaOfConcernValue={values.concerns[index]}
                                employees={employees
                                  .filter((emp) => {
                                    const selectedEmployeeId =
                                      values.concerns[index].teamMember;
                                    return (
                                      emp.id === selectedEmployeeId ||
                                      !emp.isInActive
                                    );
                                  })
                                  .map((emp) => ({
                                    label: formatName(
                                      emp.firstName,
                                      null,
                                      emp.lastName,
                                      null,
                                    ),
                                    id: emp.id,
                                  }))}
                                index={index}
                                onDelete={(i: number) => {
                                  const concern = values.concerns[i];
                                  if (concern.concernId === null) {
                                    if (values.concerns.length === 1) {
                                      values.concerns = [
                                        {
                                          concernId: null,
                                          areaOfConcern: null,
                                          teamMember: null,
                                          comments: null,
                                        },
                                      ];
                                      formikRef.current?.setValues(values);
                                    } else {
                                      values.concerns = values.concerns.filter(
                                        (___, id) => i != id,
                                      );
                                      setNumOfConcerns(numOfConcerns - 1);
                                      formikRef.current?.setValues(values);
                                    }
                                  } else {
                                    deleteConcern(concern.concernId);
                                  }
                                }}
                                disableLine={numOfConcerns != index + 1}
                                handleDataChange={(
                                  i: number,
                                  concerns: AreaOfConcerns,
                                ) => {
                                  values.concerns[i] = concerns;
                                  formikRef.current?.setValues(values);
                                }}
                              />
                            );
                          })}
                          <ButtonComponent
                            disabled={uploadedToTBR}
                            labelId="addAnotherConcernText"
                            defaultLabelId="+ Add Another Concern"
                            className="pl-sm"
                            sxProps={{
                              color: '#00C6B8',
                              textTransform: 'none',
                              textDecoration: 'underline',
                              textUnderlineOffset: '4px',
                              cursor:
                                numOfConcerns < 5 ? 'pointer' : 'not-allowed',
                              opacity: numOfConcerns < 5 ? '1' : '0.5',
                              fontWeight: 'bold',
                            }}
                            onClick={() => {
                              if (numOfConcerns < 5) {
                                values.concerns.push({
                                  concernId: null,
                                  areaOfConcern: null,
                                  teamMember: null,
                                  comments: null,
                                });
                                formikRef.current?.setValues(values);
                                setNumOfConcerns(numOfConcerns + 1);
                              }
                            }}
                          />
                        </Box>
                        <Box component="div" className="mt-lg pb-md">
                          <MediumTypography
                            labelid="signatureText"
                            defaultlabel="Signature"
                            className="mainText-xlg pt-md pb-md"
                          />

                          {values.signature?.name && (
                            <Box className="flex__">
                              <MediumTypography
                                labelid="EICS.Name"
                                defaultlabel="Name:"
                              />
                              <MediumTypography
                                label={values.signature.name ?? ''}
                                className="ml-sm"
                              />
                            </Box>
                          )}

                          {values.signature?.date && (
                            <Box className="flex__">
                              <MediumTypography
                                labelid="dateLabel"
                                defaultlabel="Date:"
                                className="pb-md"
                              />
                              <MediumTypography
                                label={
                                  values.signature.date
                                    ? moment(values.signature.date as string)
                                        .utc()
                                        .local()
                                        .format(LOCAL_DATE_TIME_FORMAT)
                                    : '-'
                                }
                                className="ml-sm pb-md"
                              />
                            </Box>
                          )}

                          {values.signature !== null &&
                            (values.signature.signatureValue !== '' ||
                              values.signature.rowkey !== '') && (
                              <ButtonComponent
                                className="btn-primary btn-cancel signature_view_Box"
                                labelId={'ProgressNoteViewSignature'}
                                defaultLabelId={'View signature'}
                                variantType="outlined"
                                LeftIcon={<Editsignature />}
                                onClick={() => setSignClicked(true)}
                              />
                            )}

                          {values.signature !== null &&
                            values.signature.signatureValue === '' &&
                            values.signature.rowkey === '' && (
                              <ButtonComponent
                                disabled={uploadedToTBR}
                                className="btn-primary btn-cancel signature_add_Box"
                                labelId="Insurance.Addsignature"
                                defaultLabelId="Add Signature"
                                variantType="outlined"
                                onClick={() => {
                                  setSignClicked(true);
                                }}
                              />
                            )}
                        </Box>
                        {signClicked && (
                          <Box component="div">
                            <SignatureComponent
                              onCancelClick={onCancelClick}
                              signaturePartitionKey={
                                values.signature.partitionkey
                              }
                              signatureRowKey={values.signature.rowkey}
                              signature={values.signature.signatureValue}
                              signatureSaveOrUPdate={onSignatureSaveOrUpdate}
                              handleSuccess={handleSuccess}
                              signClicked={signClicked}
                              hideRemoveButton={uploadedToTBR}
                              resetCallBackForTypeEmployee={
                                resetCallBackForTypeEmployee
                              }
                              type="Employee"
                            />
                          </Box>
                        )}
                      </Box>
                    </Card>
                  </Box>
                </>

                <Box>
                  <Grid
                    className="flex__ justifyContent-FlexEnd pt-md pb-lg "
                    container
                    direction="row"
                    alignItems="right"
                  >
                    <Grid item>
                      <ButtonComponent
                        disabled={uploadedToTBR}
                        className="btn-primary btn-cancel mr-md"
                        variantType="contained"
                        labelId="Clientpage.cancelbtn"
                        defaultLabelId="Cancel"
                        sxProps={{ display: isScreenLocked ? 'none' : 'auto' }}
                        onClick={() => {
                          if (
                            _.isEqual(clinicalJudgementInitialValue, values) &&
                            selectedTeam === null
                          ) {
                            navigate(BATELLE_DEVELOPMENT_INVENTORY_2);
                          } else {
                            setShowCancelDialog(true);
                          }
                        }}
                      />

                      <ButtonComponent
                        disabled={uploadedToTBR}
                        sxProps={{ display: isScreenLocked ? 'none' : 'auto' }}
                        className="btn-primary btn_width_90px btn-submit"
                        variantType="contained"
                        labelId={
                          clinicalJudgementInitialValue.id
                            ? 'clientpage.Update'
                            : 'ProgressNote.Save'
                        }
                        defaultLabelId={
                          clinicalJudgementInitialValue.id ? 'Update' : 'Save'
                        }
                        onClick={() => {
                          if (
                            values.additionalTools?.tools.some((val) =>
                              datePickerMinDateMaxDateValidate(
                                val.dateAdministered,
                              ),
                            )
                          ) {
                            return null;
                          } else {
                            formikRef.current?.submitForm();
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </>
            );
          }}
        </Formik>
      </Box>
    </Box>
  );
};

export default ClinicalJudgementEligibility;
